import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, CircularProgress, Divider, Stack, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import { sectionHeadingSx } from '../utils/editor-theme';
import type { AutomationFailure } from '../services/floorplan-automation.service';
import {
  cleaningTotalOf,
  type CleaningReport,
  type FloorPlanSuggestions,
} from '../models/floorplan-automation';

interface Props {
  readonly suggestions?: FloorPlanSuggestions;
  readonly cleaning?: CleaningReport;
  readonly busy: boolean;
  readonly failed?: AutomationFailure;
  /** True while the editor holds unsaved edits. */
  readonly dirty: boolean;
  readonly onRun: () => void;
  readonly onPlaceDesks: () => void;
  readonly onDrawRooms: () => void;
  readonly onRelink: () => void;
  readonly onPreviewCleaning: () => void;
  readonly onApplyCleaning: () => void;
}

/**
 * What the automation found, and what to do about it.
 *
 * Every suggestion here is applied into the editor's document rather than by the server: it
 * becomes an ordinary edit, undoable, saved when the admin saves. That is not ceremony —
 * placing a desk creates a bookable seat and relinking one moves capacity between rooms, and
 * the save path is where that arithmetic already lives.
 *
 * Cleaning is the exception and says so: it writes on the server, immediately, because it
 * touches geometry the editor cannot model. It is offered only as a dry run first.
 */
const AutomatePanel = ({
  suggestions,
  cleaning,
  busy,
  failed,
  dirty,
  onRun,
  onPlaceDesks,
  onDrawRooms,
  onRelink,
  onPreviewCleaning,
  onApplyCleaning,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  // Only the desks that are drawn inside a room can be placed: a desk without one is drawn
  // but unbookable, which is the state this tool exists to get rid of rather than create.
  const placeableDesks = (suggestions?.desks ?? []).filter((desk) => desk.suggestedRoomId !== null);
  const roomCount = suggestions?.rooms.length ?? 0;
  const relinkCount = suggestions?.workplaceRooms.length ?? 0;

  const section = (
    titleKey: string,
    explanationKey: string,
    count: number,
    actionKey: string,
    onAction: () => void,
    disabledReasonKey?: string,
  ): React.JSX.Element => (
    <Stack spacing={0.5}>
      <Typography sx={sectionHeadingSx}>{t(titleKey, { count })}</Typography>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t(disabledReasonKey && count > 0 ? disabledReasonKey : explanationKey)}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={count === 0 || busy || disabledReasonKey !== undefined}
        onClick={onAction}
      >
        {t(actionKey, { count })}
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={2} sx={{ p: 2, minHeight: 0, overflowY: 'auto' }}>
      <Button size="small" variant="contained" onClick={onRun} disabled={busy}>
        {busy ? <CircularProgress size={16} /> : t('floorplan-editor-automate-run')}
      </Button>

      {failed && (
        <Alert severity={failed === 'forbidden' ? 'warning' : 'error'} sx={{ py: 0 }}>
          {t(failed === 'forbidden' ? 'floorplan-editor-rooms-forbidden' : 'floorplan-editor-automation-failed')}
        </Alert>
      )}

      {suggestions && suggestions.unitsPerMetre === null && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('floorplan-editor-automate-needs-scale')}
        </Alert>
      )}

      {suggestions && (
        <>
          {section(
            'floorplan-editor-automate-desks',
            'floorplan-editor-automate-desks-hint',
            placeableDesks.length,
            'floorplan-editor-automate-place-desks',
            onPlaceDesks,
          )}

          <Divider />

          {section(
            'floorplan-editor-automate-rooms',
            'floorplan-editor-automate-rooms-hint',
            roomCount,
            'floorplan-editor-automate-draw-rooms',
            onDrawRooms,
          )}

          <Divider />

          {section(
            'floorplan-editor-automate-relink',
            'floorplan-editor-automate-relink-hint',
            relinkCount,
            'floorplan-editor-automate-apply-relink',
            onRelink,
          )}
        </>
      )}

      <Divider />

      <Stack spacing={0.5}>
        <Typography sx={sectionHeadingSx}>{t('floorplan-editor-automate-clean')}</Typography>
        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
          {t('floorplan-editor-automate-clean-hint')}
        </Typography>

        <Button size="small" variant="outlined" onClick={onPreviewCleaning} disabled={busy}>
          {t('floorplan-editor-automate-clean-preview')}
        </Button>

        {cleaning && !cleaning.changedAnything && (
          <Alert severity="success" sx={{ py: 0 }}>{t('floorplan-editor-automate-clean-nothing')}</Alert>
        )}

        {cleaning?.changedAnything && (
          <>
            {cleaning.layers
              .filter((layer) => layer.droppedEmpty + layer.closedRings + layer.addedIdentifiers + layer.strippedFields > 0)
              .map((layer) => (
                <Typography key={layer.layer} variant="caption">
                  {t('floorplan-editor-automate-clean-layer', {
                    layer: layer.layer,
                    dropped: layer.droppedEmpty,
                    closed: layer.closedRings,
                    ids: layer.addedIdentifiers,
                    stripped: layer.strippedFields,
                  })}
                </Typography>
              ))}

            {cleaning.dryRun ? (
              <>
                {/* Cleaning writes on the server there and then. Doing that underneath unsaved
                    edits means the next save is made against a plan that has since changed,
                    which the concurrency check refuses — so it is refused here instead, where
                    the reason can be given. */}
                {dirty && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {t('floorplan-editor-automate-clean-save-first')}
                  </Alert>
                )}

                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={onApplyCleaning}
                  disabled={busy || dirty}
                >
                  {t('floorplan-editor-automate-clean-apply', { count: cleaningTotalOf(cleaning) })}
                </Button>
              </>
            ) : (
              <Alert severity="success" sx={{ py: 0 }}>{t('floorplan-editor-automate-clean-applied')}</Alert>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default AutomatePanel;
